import React, { useRef, useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import useForm from 'react-hook-form';
import InputMask from 'react-input-mask';
import Select from 'react-select';
/* components */
import FormHeader from '../../components/FormHeader';
import ErrorMsgField from '../../components/ErrorMsgField';
import MultiSelect from '../../components/MultiSelect';
import AlertMsg from '../../components/AlertMsg';
/* actions */
import { getAllDevices } from '../../redux/actions/devices';
import { getAllCompanies } from '../../redux/actions/companies';
import {
  getAllRecordPorts,
  editRecordPortsIP
} from '../../redux/actions/recordPorts';
/* form-validation */
import schema from '../../form-validation/ipRecorder';
import { idCompany4Case } from '../../utils/defaultPermissions';
import {
  customStyle,
  customThemes,
  customFilter,
  firstOption
} from '../../utils/select';

function IP () {
  const INITIAL_VALUES = {
    porttype: '',
    idsExt: [],
    numbersExt: [],
    MACs: [],
    company: ''
  };
  const labelDevices = {
    2: 'SIP',
    3: 'MGCP'
  };
  const sortable = (a, b) => {
    const n1 = Number(a.label.split(' | ')[0], 10);
    const n2 = Number(b.label.split(' | ')[0], 10);
    return n1 < n2 ? -1 : n1 > n2 ? 1 : 0;
  };
  const { register, handleSubmit, errors } = useForm({
    validationSchema: schema
  });
  const submitButton = useRef();
  const dispatch = useDispatch();
  const sessionUser = useSelector(state => state.authentications.user);
  const company = sessionUser.company._id;
  const devices = useSelector(state => state.devices.data);
  const recordPorts = useSelector(state => state.recordPorts);
  const companies = useSelector(state => state.companies.data);
  const [data, setData] = useState({ ...INITIAL_VALUES });
  const [selectedOptions, setSelectedOptions] = useState({ devices: [] });
  const [extensionsOptions, setExtensionsOptions] = useState([]);
  const [ipRecorder, setIpRecorder] = useState(0);
  const [companyOptions, setCompanyOptions] = useState([firstOption]);

  useEffect(() => {
    if (companies.length) {
      const options = companies
        .map(company => ({
          value: `${company._id}`,
          quantity: company.licenses.ipRecorder,
          label: `${company.name}`,
          company: `${company._id}`,
          sortValue: `${company.name}`
        }))
        .sort(sortable);

      options.unshift(firstOption);
      setCompanyOptions([...options]);
    }
  }, [companies, setCompanyOptions]);

  const submit = () => {
    let permitSubmit = true;
    if (String(data.porttype) === '3') {
      const fields = document.getElementsByName('mac-address');
      const MACs = [];
      for (const field of fields) {
        const value = field.value.toUpperCase();
        MACs.push(value.replace(/:/g, ''));
        const regex = /^([0-9A-F]{2}[:-]){5}([0-9A-F]{2})$/;
        if (regex.test(value)) {
          field.style.borderColor = '#ced4da';
        } else {
          field.style.borderColor = '#d4565f';
          permitSubmit = false;
        }
      }
      const blockModifyExts = devices
        .filter(device => device.type === 'E')
        .map(device => device.number);
      const oldRecordPorts = recordPorts.data.filter(
        recordPort =>
          parseInt(recordPort.porttype) === 3 &&
          !blockModifyExts.includes(recordPort.device)
      );
      const oldIdsExt = oldRecordPorts.map(recordPort => recordPort.deviceId);
      const oldNumbersExt = oldRecordPorts.map(recordPort => recordPort.device);
      const oldMACsExt = oldRecordPorts.map(recordPort => recordPort.mac);
      const newData = {
        ...data,
        idsExt: data.idsExt.concat(oldIdsExt),
        numbersExt: data.numbersExt.concat(oldNumbersExt),
        MACs: MACs.concat(oldMACsExt)
      };
      if (permitSubmit) {
        dispatch(editRecordPortsIP(newData, submitButton.current));
      }
    } else {
      const blockModifyExts = devices
        .filter(device => device.type === 'E')
        .map(device => device.number);
      const oldRecordPorts = recordPorts.data.filter(
        recordPort =>
          parseInt(recordPort.porttype) === 2 &&
          !blockModifyExts.includes(recordPort.device)
      );
      const oldIdsExt = oldRecordPorts.map(recordPort => recordPort.deviceId);
      const oldNumbersExt = oldRecordPorts.map(recordPort => recordPort.device);
      const newData = {
        ...data,
        idsExt: data.idsExt.concat(oldIdsExt),
        numbersExt: data.numbersExt.concat(oldNumbersExt)
      };
      delete newData.MACs;
      dispatch(editRecordPortsIP(newData, submitButton.current));
    }
  };

  useEffect(() => {
    dispatch(getAllRecordPorts());
    dispatch(getAllDevices());
    dispatch(
      getAllCompanies(idCompany4Case === company ? {} : { _id: company })
    );
  }, [dispatch]);

  useEffect(() => {
    if (devices.length) {
      const extensions = devices
        .filter(device => device.type === 'E')
        .map(device => ({
          value: device._id,
          number: device.number,
          mac: '',
          label: `${device.number} | Dpto: ${
            device.department.name
          } - Id: ${String(device._id).slice(-5)}`
        }))
        .sort(sortable);

      setExtensionsOptions([...extensions]);
    }
  }, [devices, setExtensionsOptions]);

  const handleMultiDeselect = (deselecteds, nameField) => {
    const options = selectedOptions[nameField].slice();
    deselecteds.forEach(option => options.splice(options.indexOf(option), 1));
    setSelectedOptions({ ...selectedOptions, [nameField]: options });
    const values = options.map(opt => opt.value);
    const labels = options.map(opt => opt.label.split(' | ')[0]);
    const macs = options.map(opt => opt.mac);
    setData({ ...data, idsExt: values, numbersExt: labels, MACs: macs });
  };

  const handleMultiSelect = (selecteds, nameField) => {
    const options = selecteds.slice();
    options.sort(sortable);
    setSelectedOptions({ ...selectedOptions, [nameField]: options });
    const values = options.map(opt => opt.value);
    const labels = options.map(opt => opt.label.split(' | ')[0]);
    const macs = options.map(opt => opt.mac);
    setData({ ...data, idsExt: values, numbersExt: labels, MACs: macs });
  };

  const updateData = portTypeValue => {
    Object.keys(selectedOptions).forEach(key =>
      setSelectedOptions({ ...selectedOptions, [key]: [] })
    );
    const macs = [];
    const currentDevices = recordPorts.data
      .sort((a, b) => a.device.localeCompare(b.device))
      .reduce((newData, item) => {
        if (String(item.porttype) === portTypeValue) {
          newData.push(item.deviceId);
          macs.push(item.mac || '');
        }
        return newData;
      }, []);
    const exts = extensionsOptions.filter(extension =>
      currentDevices.includes(extension.value)
    );
    const extensions = exts.map(ext => {
      const dvc = recordPorts.data.find(device => {
        return device.deviceId === ext.value ? device.mac : null;
      });
      ext.mac = dvc && dvc.mac;
      return ext;
    });
    setSelectedOptions({ ...selectedOptions, devices: extensions });
    const values = extensions.map(opt => opt.value);
    const labels = extensions.map(opt => opt.label.split(' | ')[0]);
    setData({
      ...data,
      porttype: portTypeValue,
      idsExt: values,
      numbersExt: labels,
      MACs: macs
    });
  };

  const handleChangePortType = evt => {
    dispatch(getAllRecordPorts());
    const { value } = evt.target;
    updateData(value);
  };

  useEffect(() => {
    if (data.porttype) {
      updateData(data.porttype);
    }
  }, [data.porttype, recordPorts.data]);

  const handleChangeMAC = evt => {
    const { id, value } = evt.target;
    const position = data.idsExt.findIndex(idExt => idExt === id);
    if (position > -1) {
      const MACs = [...data.MACs];
      MACs[position] = value;
      setData({ ...data, MACs });
      const options = selectedOptions.devices.map(option =>
        option.value === id ? { ...option, mac: value } : { ...option }
      );
      setSelectedOptions({ ...selectedOptions, devices: options });
    }
  };

  return (
    <div className="container-fluid">
      {recordPorts.editSuccess && (
        <AlertMsg
          type="success"
          msg="Portas de gravação atualizada com sucesso"
        />
      )}
      {recordPorts.editError && (
        <AlertMsg
          type="danger"
          msg="Falha verifique o total de ramais selecionados SIP e MGCP"
        />
      )}
      {selectedOptions.devices.length > ipRecorder && (
        <AlertMsg
          type="danger"
          msg="Quantidade de dispositivos selecionados excede o total de licenças"
        />
      )}
      {!recordPorts.editSuccess &&
        !recordPorts.editError &&
        !(selectedOptions.devices.length > ipRecorder) && (
        <AlertMsg type="info" msg="Área configuração portas de gravação" />
      )}
      <FormHeader title="Gravação IP" />
      <h5>Qtd. Licenças de Gravação: {ipRecorder}</h5>
      <form onSubmit={handleSubmit(submit)}>
        <div className="form-group">
          <div className="form-group form-inline mb-0">
            <label className="col-sm-12 d-inline p-0 mb-2" htmlFor="company">
              Empresa
              <span className="field-required">*</span>
            </label>
            <Select
              className="col p-0"
              name="company"
              ref={register}
              onChange={evt => {
                const { quantity, company } = evt;
                const newValue = quantity || 0;
                setIpRecorder(newValue);
                if (!newValue) {
                  setData({ ...data, porttype: '', company });
                  setSelectedOptions({ ...selectedOptions, devices: [] });
                } else {
                  setData({ ...data, company });
                }
              }}
              value={companyOptions.find(
                company => company.value === data.company
              )}
              options={companyOptions}
              isSearchable={true}
              theme={customThemes}
              styles={customStyle}
              filterOption={customFilter}
              noOptionsMessage={() => 'Nenhum registro encontrado'}
            />
          </div>
          {errors.company && errors.company.type === 'required' && (
            <ErrorMsgField type="required" />
          )}
        </div>
        <div className="form-group">
          <label htmlFor="porttype">
            Tipo<span className="field-required">*</span>
          </label>
          <select
            className="form-control"
            id="porttype"
            name="porttype"
            value={data.porttype}
            onChange={handleChangePortType}
            disabled={!data.company}
            ref={register}
          >
            <option value="">-- Selecione --</option>
            {Object.keys(labelDevices).map(key => {
              return (
                <option key={key} value={key}>
                  {labelDevices[key].toUpperCase()}
                </option>
              );
            })}
          </select>
          {errors.porttype && errors.porttype.type === 'required' && (
            <ErrorMsgField type="required" />
          )}
          {errors.porttype && errors.porttype.type === 'typeError' && (
            <ErrorMsgField type="required" />
          )}
        </div>
        <MultiSelect
          avaliableOptions={extensionsOptions}
          selectedOptions={selectedOptions.devices}
          handleMultiDeselect={options =>
            handleMultiDeselect(options, 'devices')
          }
          handleMultiSelect={options => handleMultiSelect(options, 'devices')}
          valueProp="value"
          textProp="label"
          title="Ramais"
          name="devices"
          ref={register}
          error={!!errors.devices}
          disabled={!data.porttype}
        />
        {String(data.porttype) === '3' && (
          <div className="form-group">
            <label>MAC Address Ramais</label>
            <div className="border mb-2">
              {selectedOptions.devices.map((device, position) => {
                const { number, mac } = device;
                const id = device.value;
                return (
                  <div key={id}>
                    <div
                      className="form-group form-row align-items-center m-1 p-2"
                      style={{
                        backgroundColor: position % 2 === 0 ? '#eee' : '#fff'
                      }}
                    >
                      <label htmlFor={id} className="m-0">
                        {number}&nbsp;&nbsp;
                      </label>
                      <div className="w-75">
                        <InputMask
                          id={id}
                          name="mac-address"
                          onChange={handleChangeMAC}
                          mask={'**:**:**:**:**:**'}
                          className="form-control text-uppercase"
                          placeholder="MAC ADDRESS"
                          formatChars={{ '*': '[0-9A-Fa-f]' }}
                          defaultValue={mac}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        <div className="row">
          <div className="container-fluid">
            <button
              type="submit"
              ref={submitButton}
              className="btn btn-primary float-right"
              disabled={selectedOptions.devices.length > ipRecorder}
            >
              Salvar
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

export default IP;
